import * as React from "react";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";
import { ActuateButton } from "../ActuateButton";
import { OrangeTextButton } from "../OrangeTextButton";
import { universalAPIErrorHandler } from "../../utils/universalAPIErrorHandler";
import {
  GenetecAlarmPollOptions,
  GenetecAlarmSetup,
  fetchGenetecAlarmSetup,
  pollGenetecAlarmSetup,
  startGenetecAlarmSetup,
} from "./genetecAlarmSetupApi";
import {
  GenetecAlarmSummary,
  recipientsSentence,
  summarizeGenetecAlarmSetup,
} from "./genetecAlarmSetupStatus";

export const GENETEC_ALARM_DIALOG_TITLE =
  "Send detections to Security Center as alarms?";

type Phase =
  | { kind: "loading" }
  | { kind: "loadError"; message: string }
  | { kind: "prompt"; error?: string }
  | { kind: "running" }
  | { kind: "done"; summary: GenetecAlarmSummary };

const errorDetail = (reason: unknown, fallback: string) => {
  const data = (reason as AxiosError<any>)?.response?.data;
  if (data && typeof data === "object") {
    if (typeof data.detail === "string") return data.detail;
    if (Array.isArray(data) && typeof data[0] === "string") return data[0];
  }
  return fallback;
};

/**
 * One dialog for both entry points (ENG-2219): the end of Genetec camera
 * auto-add, and "Set up Security Center alarms" in the site's actions menu.
 *
 * Set up alarms → POST genetec_alarm_setup → poll action_status for the
 * Command it dispatched → show the outcome. Closing (or unmounting) aborts
 * the poll; the job itself keeps running and lands in Command History.
 */
export const GenetecAlarmSetupDialog = ({
  open,
  siteId,
  onClose,
  setup: providedSetup,
  showStreamCoverage = false,
  navigate,
  pollOptions,
}: {
  open: boolean;
  siteId: string | number;
  onClose: () => void;
  /** Already-fetched GET response; fetched on open when omitted. */
  setup?: GenetecAlarmSetup | null;
  /** Site settings: show "Alarms are on for X of Y streams". */
  showStreamCoverage?: boolean;
  /** When given, API errors go through universalAPIErrorHandler (token refresh). */
  navigate?: NavigateFunction;
  pollOptions?: Omit<GenetecAlarmPollOptions, "signal">;
}) => {
  const [setup, setSetup] = React.useState<GenetecAlarmSetup | null>(
    providedSetup ?? null
  );
  const [phase, setPhase] = React.useState<Phase>(
    providedSetup ? { kind: "prompt" } : { kind: "loading" }
  );
  const abortRef = React.useRef<AbortController | null>(null);

  const newController = () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    return controller;
  };

  React.useEffect(() => () => abortRef.current?.abort(), []);

  const handleApiError = (
    reason: unknown,
    retry: () => void,
    onError: () => void
  ) => {
    if (navigate) {
      universalAPIErrorHandler(reason as AxiosError, navigate, retry, onError);
    } else {
      onError();
    }
  };

  const load = React.useCallback(() => {
    const controller = newController();
    setPhase({ kind: "loading" });
    fetchGenetecAlarmSetup(siteId, controller.signal).then(
      (data) => {
        if (controller.signal.aborted) return;
        setSetup(data);
        setPhase({ kind: "prompt" });
      },
      (reason) => {
        if (controller.signal.aborted) return;
        handleApiError(reason, load, () =>
          setPhase({
            kind: "loadError",
            message: errorDetail(
              reason,
              "Couldn't load the Security Center alarm settings. Please try again."
            ),
          })
        );
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  // Reset every time the dialog opens so a re-run starts from the prompt.
  React.useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      return;
    }
    if (providedSetup) {
      setSetup(providedSetup);
      setPhase({ kind: "prompt" });
    } else {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const runSetup = () => {
    const controller = newController();
    setPhase({ kind: "running" });
    startGenetecAlarmSetup(siteId).then(
      async (dispatch) => {
        if (controller.signal.aborted) return;
        const outcome = await pollGenetecAlarmSetup(
          siteId,
          dispatch.command_id,
          {
            ...pollOptions,
            signal: controller.signal,
          }
        );
        if (outcome === null || controller.signal.aborted) return;
        setPhase({
          kind: "done",
          summary: summarizeGenetecAlarmSetup(outcome),
        });
      },
      (reason) => {
        if (controller.signal.aborted) return;
        handleApiError(reason, runSetup, () =>
          setPhase({
            kind: "prompt",
            error: errorDetail(
              reason,
              "Couldn't start the alarm setup. Please try again."
            ),
          })
        );
      }
    );
  };

  const close = () => {
    abortRef.current?.abort();
    onClose();
  };

  const alarmNames = setup?.alarm_names ?? [];
  const canSetUp = Boolean(setup?.is_genetec) && alarmNames.length > 0;

  return (
    <Dialog
      open={open}
      onClose={close}
      aria-labelledby="genetec-alarm-setup-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="genetec-alarm-setup-title" sx={{ fontFamily: "Mulish" }}>
        {GENETEC_ALARM_DIALOG_TITLE}
      </DialogTitle>
      <DialogContent sx={{ fontFamily: "Mulish" }}>
        {phase.kind === "loading" && (
          <div className="flex justify-center py-4">
            <CircularProgress color="warning" size={28} />
          </div>
        )}

        {phase.kind === "loadError" && (
          <Alert severity="error">{phase.message}</Alert>
        )}

        {phase.kind === "prompt" && setup && (
          <div className="font-mulish text-actuate-blue">
            {canSetUp ? (
              <p>
                We&apos;ll create these alarms on your Security Center:{" "}
                <strong data-testid="genetec-alarm-names">
                  {alarmNames.join(", ")}
                </strong>{" "}
                (re-trigger delay 0). After that, add recipients to them in
                Config Tool → Alarms → Recipients.
              </p>
            ) : (
              <p>
                {setup.is_genetec
                  ? "None of this site's streams have a Security Center alarm name yet, so there is nothing to set up."
                  : "Security Center alarms are only available for Genetec sites."}
              </p>
            )}
            {showStreamCoverage && (
              <p className="pt-3" data-testid="genetec-alarm-coverage">
                Alarms are on for {setup.streams_alarm_on} of{" "}
                {setup.streams_total} stream
                {setup.streams_total === 1 ? "" : "s"}.
              </p>
            )}
            {phase.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {phase.error}
              </Alert>
            )}
          </div>
        )}

        {phase.kind === "running" && (
          <div className="flex items-center gap-3 py-2 font-mulish text-actuate-blue">
            <CircularProgress color="warning" size={24} />
            <p>
              Setting up alarms on Security Center. This usually takes under a
              minute.
            </p>
          </div>
        )}

        {phase.kind === "done" && (
          <Alert severity={phase.summary.severity}>
            <p>{phase.summary.headline}</p>
            {phase.summary.failedAlarms.length > 0 && (
              <ul className="list-disc pl-5 pt-1">
                {phase.summary.failedAlarms.map((alarm) => (
                  <li key={alarm.name}>
                    <strong>{alarm.name}</strong>
                    {alarm.detail ? ` — ${alarm.detail}` : ""}
                  </li>
                ))}
              </ul>
            )}
            {phase.summary.severity === "warning" &&
              phase.summary.needsRecipients.length > 0 && (
                <p className="pt-1">
                  {recipientsSentence(phase.summary.needsRecipients)}
                </p>
              )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {phase.kind === "prompt" && (
          <>
            <OrangeTextButton onClick={close} text="Not now" disabled={false} />
            <ActuateButton
              onClick={runSetup}
              text="Set up alarms"
              disabled={!canSetUp}
            />
          </>
        )}
        {phase.kind === "loadError" && (
          <>
            <OrangeTextButton onClick={close} text="Close" disabled={false} />
            <OrangeTextButton onClick={load} text="Retry" disabled={false} />
          </>
        )}
        {phase.kind === "loading" && (
          <OrangeTextButton onClick={close} text="Not now" disabled={false} />
        )}
        {(phase.kind === "done" || phase.kind === "running") && (
          // Closing while running only stops watching; the job keeps going.
          <OrangeTextButton onClick={close} text="Close" disabled={false} />
        )}
      </DialogActions>
    </Dialog>
  );
};
